import {
	type Markup,
	createResizeReferenceGeometryData,
	isGeometryDataDegenerateOnAnyAxis,
	isResizeReferenceMarkupType,
} from "../../../../../../data/models/Markup";
import {MarkupType} from "../../../../../../generated/api/base";
import {Constants} from "../Constants";
import type {Markup3D} from "../elements3d/markups/abstract/Markup3D";
import {MarkupAB2D} from "../elements3d/markups/abstract/MarkupAB2D";
import type {ISelectionHandleAction} from "../managers/spaceitems/SelectionHandleManager";
import type {PointDouble} from "../../../../../../generated/api/base";
import {THREEUtils} from "../../../../../../utils/THREEUtils";
import {KeyboardListener} from "../../../../../../utils/interaction/key/KeyboardListener";
import {getMarkupSelectionPadding} from "../managers/spaceitems/SelectionHandleUtils";
import type {SelectionHandleCorner} from "../managers/spaceitems/SelectionHandleUtils";

const getOppositeCorner = (corner: "topLeft" | "topRight" | "bottomRight" | "bottomLeft") => {
	switch (corner) {
		case "topLeft":
			return "bottomRight";
		case "topRight":
			return "bottomLeft";
		case "bottomRight":
			return "topLeft";
		default:
			return "topRight";
	}
};

interface IScaleState {
	action: ISelectionHandleAction;
	item: Markup3D;
	geometryData: PointDouble[];
	unrotatedGeometryData: PointDouble[];
	rotationPivot: PointDouble;
	orientation: number;
	selectionPadding?: number;
	fixedCornerPoint?: PointDouble;
	target?: PointDouble;
	textOffset?: PointDouble;
	textCenter?: PointDouble;
	textOrientation?: number;
	textSize?: PointDouble;
	activeCorner?: SelectionHandleCorner;
	initialResizeReferenceGeometryData?: PointDouble[];
}

export class ScaleChanger {
	private _state: IScaleState = null;
	private readonly _updateMarkupTextTransformations: () => void;

	constructor(updateMarkupTextTransformations: () => void) {
		this._updateMarkupTextTransformations = updateMarkupTextTransformations;
	}

	public startScaling(action: ISelectionHandleAction) {
		const item = action.item;

		if (!item) {
			return;
		}

		const model = item.modelData as Markup;

		this._state = {
			action,
			item,
			geometryData: THREEUtils.cloneGeometryData(model.geometryData),
			unrotatedGeometryData: THREEUtils.getRotatedVertices(THREEUtils.cloneGeometryData(model.geometryData), -item.orientation),
			rotationPivot: THREEUtils.getCenterOfBoundingBox(THREEUtils.calculateBox(model.geometryData)),
			orientation: item.orientation,
			selectionPadding: this.getSelectionPadding(item),
			fixedCornerPoint:
				action.type === "scale" && action.corner && item instanceof MarkupAB2D
					? this.getMarkupAB2DCornerPoint(item, getOppositeCorner(action.corner))
					: null,
			target: item.target ? {...item.target} : null,
			textOffset: {...item.textOffset},
			textCenter: item.getTextWorldPosition(),
			textOrientation: item.orientation + item.textOrientation,
			textSize: item.textSelectionSize ? {...item.textSelectionSize} : null,
			activeCorner: action.corner,
			initialResizeReferenceGeometryData: model.resizeReferenceGeometryData ? THREEUtils.cloneGeometryData(model.resizeReferenceGeometryData) : null,
		};
	}

	public updateXY(cursorX: number, cursorY: number) {
		if (!this._state) {
			return;
		}

		switch (this._state.action.type) {
			case "scale":
				this.scaleMarkupBox(cursorX, cursorY);
				break;
			case "scaleLineEndpoint":
				this.scaleLineEndpoint(cursorX, cursorY);
				break;
			case "scaleText":
				this.scaleMarkupText(cursorX, cursorY);
				break;
			case "anchor":
				this.moveCalloutAnchor(cursorX, cursorY);
				break;
			case "moveTextAnchor":
				this.moveTextAnchor(cursorX, cursorY);
				break;
		}
	}

	public stopScaling() {
		this.updateResizeReferenceGeometryDataAfterScaling();

		if (this._state?.item) {
			this._state.item.itemManager.updateItems([this._state.item], false);
		}

		this._state = null;
	}

	public get isScaling() {
		return !!this._state;
	}

	public get action() {
		return this._state?.action ?? null;
	}

	public get item() {
		return this._state?.item ?? null;
	}

	public get currentCorner() {
		return this._state?.activeCorner ?? this._state?.action.corner ?? null;
	}

	private scaleMarkupBox(cursorX: number, cursorY: number) {
		const {item, action, unrotatedGeometryData, orientation, rotationPivot, selectionPadding} = this._state;

		if (!action.corner) {
			return;
		}

		if (item instanceof MarkupAB2D) {
			this.scaleMarkupAB2D(cursorX, cursorY);
			return;
		}

		const originalBBox = THREEUtils.calculateBox(unrotatedGeometryData);
		const fixedCorner = getOppositeCorner(action.corner);
		const grabbedPoint = this.getBoxCornerPoint(originalBBox, action.corner);
		const fixedPoint = this.getBoxCornerPoint(originalBBox, fixedCorner);
		const pointer = this.getClampedMarkupPointer(
			THREEUtils.getRotatedVertex({x: cursorX, y: cursorY}, -orientation, rotationPivot),
			fixedPoint,
			selectionPadding,
		);
		this._state.activeCorner = this.getCornerForDelta(pointer.x - fixedPoint.x, pointer.y - fixedPoint.y, this._state.activeCorner ?? action.corner);
		const model = item.modelData as Markup;

		if (
			isResizeReferenceMarkupType(item.type) &&
			isGeometryDataDegenerateOnAnyAxis(unrotatedGeometryData) &&
			model.resizeReferenceGeometryData?.length
		) {
			const rebuiltGeometry = this.rebuildCollapsedDrawingGeometry(model.resizeReferenceGeometryData, fixedPoint, pointer, fixedCorner, orientation);

			model.setGeometryData(rebuiltGeometry);
			this.restoreResizeReferenceGeometryData(model);
			item.updateByModel(model, false);
			this._updateMarkupTextTransformations();

			return;
		}

		const scaledUnrotatedGeometry = this.scaleGeometryAroundFixedPoint(unrotatedGeometryData, fixedPoint, grabbedPoint, pointer);
		const rotatedGeometry = THREEUtils.getRotatedVertices(scaledUnrotatedGeometry, orientation, rotationPivot);

		model.setGeometryData(rotatedGeometry);
		if ([MarkupType.TextBox, MarkupType.Callout].includes(item.type)) {
			model.setIsSizeFixed(true);
		}
		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private scaleMarkupAB2D(cursorX: number, cursorY: number) {
		const {item, action, fixedCornerPoint, orientation, selectionPadding} = this._state;

		if (!(item instanceof MarkupAB2D) || !action.corner) {
			return;
		}

		const model = item.modelData as Markup;
		const fixedPoint = fixedCornerPoint ?? this.getMarkupAB2DCornerPoint(item, getOppositeCorner(action.corner));
		let localCursor = this.getClampedMarkupPointer(
			THREEUtils.getRotatedVertex({x: cursorX, y: cursorY}, -orientation, fixedPoint),
			fixedPoint,
			selectionPadding,
		);
		this._state.activeCorner = this.getCornerForDelta(
			localCursor.x - fixedPoint.x,
			localCursor.y - fixedPoint.y,
			this._state.activeCorner ?? action.corner,
		);

		if (KeyboardListener.isAltDown) {
			const delta = {
				x: localCursor.x - fixedPoint.x,
				y: localCursor.y - fixedPoint.y,
			};
			const size = Math.max(Math.abs(delta.x), Math.abs(delta.y));

			localCursor = {
				x: fixedPoint.x + Math.sign(delta.x || 1) * size,
				y: fixedPoint.y + Math.sign(delta.y || 1) * size,
			};
		}

		const localMin = {
			x: Math.min(fixedPoint.x, localCursor.x),
			y: Math.min(fixedPoint.y, localCursor.y),
		};
		const localMax = {
			x: Math.max(fixedPoint.x, localCursor.x),
			y: Math.max(fixedPoint.y, localCursor.y),
		};
		const worldA = THREEUtils.getRotatedVertex(localMin, orientation, fixedPoint);
		const worldB = THREEUtils.getRotatedVertex(localMax, orientation, fixedPoint);

		model.setGeometryData([worldA, worldB]);

		if (item.type === MarkupType.TextBox) {
			model.setIsSizeFixed(true);
		}

		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private scaleLineEndpoint(cursorX: number, cursorY: number) {
		const {item, action, geometryData} = this._state;

		if (action.endpointIndex == null) {
			return;
		}

		const model = item.modelData as Markup;
		const nextGeometry = THREEUtils.cloneGeometryData(geometryData);

		nextGeometry[action.endpointIndex] = {x: cursorX, y: cursorY};
		model.setGeometryData(nextGeometry);
		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private scaleMarkupText(cursorX: number, cursorY: number) {
		const {item, action, textCenter, textOrientation, textSize} = this._state;

		if (!action.corner || !textCenter || !textSize) {
			return;
		}

		const bbox = {
			min: {
				x: textCenter.x - textSize.x / 2,
				y: textCenter.y - textSize.y / 2,
			},
			max: {
				x: textCenter.x + textSize.x / 2,
				y: textCenter.y + textSize.y / 2,
			},
		};
		const fixedCorner = getOppositeCorner(action.corner);
		const fixedPoint = this.getBoxCornerPoint(bbox, fixedCorner);
		const nextTextBox = this.getOrientedBoxFromFixedPoint({x: cursorX, y: cursorY}, fixedPoint, textOrientation);
		const localCursor = THREEUtils.getRotatedVertex({x: cursorX, y: cursorY}, -textOrientation, fixedPoint);

		this._state.activeCorner = this.getCornerForDelta(
			localCursor.x - fixedPoint.x,
			localCursor.y - fixedPoint.y,
			this._state.activeCorner ?? action.corner,
		);
		const centerDelta = {
			x: nextTextBox.center.x - item.position.x,
			y: nextTextBox.center.y - item.position.y,
		};
		const unrotatedTextOffset = THREEUtils.getRotatedVertex(centerDelta, -item.orientation, {x: 0, y: 0});
		const model = item.modelData as Markup;

		model.setTextBoxSize(nextTextBox.size);
		model.setTextOffset(unrotatedTextOffset);
		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private moveCalloutAnchor(cursorX: number, cursorY: number) {
		const {item} = this._state;
		const model = item.modelData as Markup;

		model.setTarget({x: cursorX, y: cursorY});
		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private moveTextAnchor(cursorX: number, cursorY: number) {
		const {item, textSize} = this._state;

		if (!textSize) {
			return;
		}

		const localAnchorOffset = THREEUtils.getRotatedVertex({x: 0, y: textSize.y / 2}, item.textOrientation, {x: 0, y: 0});
		const worldAnchorOffset = {
			x: cursorX - item.position.x,
			y: cursorY - item.position.y,
		};
		const localAnchorPosition = THREEUtils.getRotatedVertex(worldAnchorOffset, -item.orientation, {x: 0, y: 0});
		const model = item.modelData as Markup;

		model.setTextOffset({
			x: localAnchorPosition.x - localAnchorOffset.x,
			y: localAnchorPosition.y - localAnchorOffset.y,
		});
		item.updateByModel(model, false);
		this._updateMarkupTextTransformations();
	}

	private scaleGeometryAroundFixedPoint(geometryData: PointDouble[], fixedPoint: PointDouble, grabbedPoint: PointDouble, pointer: PointDouble) {
		const originalDeltaX = grabbedPoint.x - fixedPoint.x;
		const originalDeltaY = grabbedPoint.y - fixedPoint.y;
		const nextDeltaX = pointer.x - fixedPoint.x;
		const nextDeltaY = pointer.y - fixedPoint.y;
		const scaleX = Math.abs(originalDeltaX) < Constants.EPSILON ? 1 : nextDeltaX / originalDeltaX;
		const scaleY = Math.abs(originalDeltaY) < Constants.EPSILON ? 1 : nextDeltaY / originalDeltaY;

		return geometryData.map((point) => ({
			x: fixedPoint.x + (point.x - fixedPoint.x) * scaleX,
			y: fixedPoint.y + (point.y - fixedPoint.y) * scaleY,
		}));
	}

	private getBoxCornerPoint(bbox: {min: PointDouble; max: PointDouble}, corner: "topLeft" | "topRight" | "bottomRight" | "bottomLeft") {
		switch (corner) {
			case "topLeft":
				return {x: bbox.min.x, y: bbox.max.y};
			case "topRight":
				return {x: bbox.max.x, y: bbox.max.y};
			case "bottomRight":
				return {x: bbox.max.x, y: bbox.min.y};
			default:
				return {x: bbox.min.x, y: bbox.min.y};
		}
	}

	private getSelectionPadding(item: Markup3D) {
		return getMarkupSelectionPadding(
			item.type,
			item.spaceViewRenderer.correctionMultiplier.current,
			item.spaceViewRenderer.toolManager.cameraControls.cameraZoomValue,
			item.lineThickness,
		);
	}

	private restoreResizeReferenceGeometryData(model: Markup) {
		const resizeReferenceGeometryData = this._state?.initialResizeReferenceGeometryData;

		if (!resizeReferenceGeometryData?.length) {
			return;
		}

		model.setResizeReferenceGeometryData(THREEUtils.cloneGeometryData(resizeReferenceGeometryData));
	}

	private updateResizeReferenceGeometryDataAfterScaling() {
		const state = this._state;

		if (!state?.item || state.action?.type !== "scale" || !isResizeReferenceMarkupType(state.item.type)) {
			return;
		}

		const model = state.item.modelData as Markup;

		if (isGeometryDataDegenerateOnAnyAxis(model.geometryData)) {
			this.restoreResizeReferenceGeometryData(model);

			return;
		}

		const resizeReferenceGeometryData = createResizeReferenceGeometryData(model.geometryData, state.item.orientation);

		if (resizeReferenceGeometryData?.length) {
			model.setResizeReferenceGeometryData(resizeReferenceGeometryData);
		}
	}

	private getMarkupAB2DCornerPoint(item: MarkupAB2D, corner: "topLeft" | "topRight" | "bottomRight" | "bottomLeft") {
		const corners = item.get4CornersAsWorldVertices();

		switch (corner) {
			case "bottomLeft":
				return corners[0];
			case "bottomRight":
				return corners[1];
			case "topRight":
				return corners[2];
			default:
				return corners[3];
		}
	}

	private getClampedMarkupPointer(pointer: PointDouble, fixedPoint: PointDouble, selectionPadding: number = 0) {
		if (selectionPadding <= 0) {
			return pointer;
		}

		return {
			x: fixedPoint.x + this.getClampedMarkupDelta(pointer.x - fixedPoint.x, selectionPadding),
			y: fixedPoint.y + this.getClampedMarkupDelta(pointer.y - fixedPoint.y, selectionPadding),
		};
	}

	private getClampedMarkupDelta(delta: number, selectionPadding: number) {
		if (Math.abs(delta) <= selectionPadding) {
			return 0;
		}

		return Math.sign(delta) * (Math.abs(delta) - selectionPadding);
	}

	private rebuildCollapsedDrawingGeometry(
		resizeReferenceGeometryData: PointDouble[],
		fixedPoint: PointDouble,
		pointer: PointDouble,
		fixedCorner: SelectionHandleCorner,
		orientation: number,
	) {
		const fixedCornerPoint = this.getNormalizedFixedCornerPoint(fixedCorner);
		const scaleX = this.getResizeReferenceAxisScale(pointer.x - fixedPoint.x, fixedCornerPoint.x);
		const scaleY = this.getResizeReferenceAxisScale(pointer.y - fixedPoint.y, fixedCornerPoint.y);
		const rebuiltUnrotatedGeometry = resizeReferenceGeometryData.map((point) => ({
			x: fixedPoint.x + (point.x - fixedCornerPoint.x) * scaleX,
			y: fixedPoint.y + (point.y - fixedCornerPoint.y) * scaleY,
		}));

		if (orientation === 0) {
			return rebuiltUnrotatedGeometry;
		}

		const center = THREEUtils.getCenterOfBoundingBox(THREEUtils.calculateBox(rebuiltUnrotatedGeometry));

		return THREEUtils.getRotatedVertices(rebuiltUnrotatedGeometry, orientation, {
			x: center.x,
			y: center.y,
		});
	}

	private getNormalizedFixedCornerPoint(corner: SelectionHandleCorner): PointDouble {
		switch (corner) {
			case "topLeft":
				return {x: 0, y: 1};
			case "topRight":
				return {x: 1, y: 1};
			case "bottomRight":
				return {x: 1, y: 0};
			default:
				return {x: 0, y: 0};
		}
	}

	private getResizeReferenceAxisScale(delta: number, fixedNormalizedCoordinate: number) {
		return fixedNormalizedCoordinate === 0 ? delta : -delta;
	}

	private getCornerForDelta(deltaX: number, deltaY: number, fallbackCorner: SelectionHandleCorner): SelectionHandleCorner {
		const fallbackHorizontal = ["topRight", "bottomRight"].includes(fallbackCorner) ? "right" : "left";
		const fallbackVertical = ["topLeft", "topRight"].includes(fallbackCorner) ? "top" : "bottom";
		const horizontal = deltaX > 0 ? "right" : deltaX < 0 ? "left" : fallbackHorizontal;
		const vertical = deltaY > 0 ? "top" : deltaY < 0 ? "bottom" : fallbackVertical;

		if (vertical === "top") {
			return horizontal === "right" ? "topRight" : "topLeft";
		}

		return horizontal === "right" ? "bottomRight" : "bottomLeft";
	}

	private getOrientedBoxFromFixedPoint(pointer: PointDouble, fixedPoint: PointDouble, orientation: number) {
		const localCursor = THREEUtils.getRotatedVertex(pointer, -orientation, fixedPoint);
		const localMin = {
			x: Math.min(fixedPoint.x, localCursor.x),
			y: Math.min(fixedPoint.y, localCursor.y),
		};
		const localMax = {
			x: Math.max(fixedPoint.x, localCursor.x),
			y: Math.max(fixedPoint.y, localCursor.y),
		};
		const localCenter = {
			x: (localMin.x + localMax.x) / 2,
			y: (localMin.y + localMax.y) / 2,
		};

		return {
			center: THREEUtils.getRotatedVertex(localCenter, orientation, fixedPoint),
			size: {
				x: Math.max(localMax.x - localMin.x, Constants.EPSILON),
				y: Math.max(localMax.y - localMin.y, Constants.EPSILON),
			},
		};
	}
}
